<template>
  <div class="flex flex-col gap-4">
    <InlineNote v-if="checkPayableTo" tone="info" data-cy="check-payable-note">
      Make check payable to
      <strong class="font-semibold">{{ checkPayableTo }}</strong>
    </InlineNote>

    <PaymentFormSelect
      v-model="receiverId"
      label="Collected by"
      placeholder="Who collected the payment?"
      :options="receiverOptions"
      :error-message="receiverIdError"
      data-cy="collected-by"
    />

    <PaymentFormInput
      v-model="form.check_number"
      label="Check number"
      placeholder="Check #"
      :error-message="firstErrorMessage(v$.form.check_number)"
      data-cy="check-number"
    />

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PaymentFormInput
        v-model="form.check_dl_number"
        label="Driver's license #"
        placeholder="DL #"
        :error-message="firstErrorMessage(v$.form.check_dl_number)"
        data-cy="check-dl-number"
      />
      <PaymentFormInput
        v-model="form.check_dl_state"
        label="License state"
        placeholder="State"
        :error-message="firstErrorMessage(v$.form.check_dl_state)"
        data-cy="check-dl-state"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import InlineNote from "@/components/PaymentModals/InlineNote.vue";
import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import PaymentFormSelect from "@/components/PaymentModals/PaymentFormSelect.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import usePaymentMethods from "./usePaymentMethods";

const props = defineProps<{
  modelValue: number | string | null;
  form: {
    check_number: string;
    check_dl_number: string;
    check_dl_state: string;
  } | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number | string | null): void;
}>();

const { paymentMethodsResponse, receivers } = usePaymentMethods();

const receiverId = computed({
  get: () => (props.modelValue ?? "") as string,
  set: (val) => emit("update:modelValue", val)
});

const receiverOptions = computed(() =>
  receivers.value.map((receiver: any) => ({
    value: `${receiver.id}_${receiver.type}`,
    label: receiver.full_name
  }))
);

const checkPayableTo = computed(() => {
  const check = paymentMethodsResponse.value.find(
    (method: any) => method.key === "check"
  );
  return check?.check_payable_to;
});

const form = computed(() => props.form!);

const v$ = useVuelidate(
  {
    form: {
      check_number: { required, $autoDirty: true },
      check_dl_number: { required, $autoDirty: true },
      check_dl_state: { required, $autoDirty: true }
    },
    receiverId: { required, $autoDirty: true }
  },
  { form, receiverId }
);

const receiverIdError = computed(() => firstErrorMessage(v$.value.receiverId));
</script>
